// qs-TextPanel.vue
<template>
  <div class="text-panel-wrapper">
    <div class="text-panel">
      <div
        v-for="(preset, index) in textPresets"
        :key="preset.id"
        class="text-item"
        :class="{ active: selectedIndex === index && modeManager.activeMode.value === 'text' }"
        @click="selectPreset(index)"
      >
        
        <div
          class="font-display"
          :style="{ fontFamily: preset.fontFamily }"
          @mousedown="startLongPress($event, index)"
          @mouseup.prevent="endLongPress($event)"
          @mouseleave="endLongPress()"
          @touchstart.passive="startLongPress($event, index)"
          @touchend.prevent="endLongPress($event)"
          @touchcancel="endLongPress()"
          @click.stop.prevent="handleFontDisplayClick(index, $event)"
        >
          T
        </div>
        
        
        <input
          v-model="inputValues[index]"
          type="number"
          min="8"
          max="200"
          class="size-input"
          @blur="handleInputBlur(index)"
          @focus="selectPreset(index)"
          @click.stop
        >
      </div>
    </div>

    
    <Teleport to="body">
      <div
        v-if="fontSelectorVisible !== null"
        ref="fontSelectorRef"
        class="font-selector"
        :style="fontSelectorStyle"
        @click.stop
      >
        <div
          v-for="font in filteredFonts"
          :key="font.value"
          class="font-option"
          :style="{ fontFamily: font.value }"
          @click.stop.prevent="selectFont(font.value, $event)"
        >
          T
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">import { useStorage } from '@vueuse/core'
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useModeManager } from '@/composables/qs-useModeManager'
import { useToolStore } from '@/stores/qs-store-tools'
import type { TextStylePreset } from '@/types/qs-types-index'

const emit = defineEmits<{
  'text-style-selected': [preset: TextStylePreset]
  'text-placement-mode': [enabled: boolean]
}>()

const modeManager = useModeManager()
const toolStore = useToolStore()

const availableFonts = [
  { label: 'Sans-serif', value: 'Helvetica, Arial, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif' },
  { label: 'Serif', value: 'Georgia, "Times New Roman", Times, serif' },
  { label: 'Monospace', value: '"Courier New", Courier, "Lucida Console", Monaco, monospace' },
  { label: 'Comic', value: '"Comic Sans MS", "Comic Sans", cursive, sans-serif' }
]

const defaultTextPresets: TextStylePreset[] = [
  {
    id: 'text-preset-1',
    name: 'Small Text',
    fontSize: 20,
    color: '#808080',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    fontStyle: 'normal'
  },
  {
    id: 'text-preset-2',
    name: 'Medium Text',
    fontSize: 40,
    color: '#808080',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    fontStyle: 'normal'
  },
  {
    id: 'text-preset-3',
    name: 'Large Text',
    fontSize: 60,
    color: '#808080',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    fontStyle: 'normal'
  }
]

const textPresets = useStorage<TextStylePreset[]>('vectorsketch-text-presets', defaultTextPresets)
const selectedIndex = ref(0)
const inputValues = ref<number[]>([])
const fontSelectorVisible = ref<number | null>(null)
const fontSelectorRef = ref<HTMLElement | null>(null)
const fontSelectorStyle = ref<{ top: string; left: string }>({ top: '0px', left: '0px' })

let longPressTimer: number | null = null
const longPressDelay = 500
const hasLongPressed = ref(false)
const longPressIndex = ref<number | null>(null)
const longPressElement = ref<HTMLElement | null>(null)

const selectedPreset = computed(() => textPresets.value[selectedIndex.value])

const filteredFonts = computed(() => {
  if (fontSelectorVisible.value === null) {
    return availableFonts
  }
  const currentPreset = textPresets.value[fontSelectorVisible.value]
  if (!currentPreset) {
    return availableFonts
  }
  return availableFonts.filter(font => font.value !== currentPreset.fontFamily)
})

const initializeInputValues = () => {
  inputValues.value = textPresets.value.map(preset => preset.fontSize)
}

const selectPreset = async (index: number) => {
  selectedIndex.value = index
  const preset = textPresets.value[index]
  if (preset) {
    const currentColor = toolStore.currentTool.color
    preset.color = currentColor
    
    try {
      const { useTextStore } = await import('@/stores/qs-store-text')
      const textStore = useTextStore()
      textStore.setCurrentTextPreset(preset)
    } catch (error) {
      console.error('Failed to set text preset:', error)
    }

    if (modeManager.activeMode.value !== 'text') {
      modeManager.activateTextMode()
    }
    
    emit('text-style-selected', preset)
    emit('text-placement-mode', true)
  }
}

const startLongPress = (event: MouseEvent | TouchEvent, index: number) => {
  if (event instanceof MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  if (fontSelectorVisible.value !== null) return

  if (longPressTimer) {
    clearTimeout(longPressTimer)
  }

  hasLongPressed.value = false
  longPressIndex.value = index
  longPressElement.value = event.currentTarget as HTMLElement

  longPressTimer = window.setTimeout(() => {
    if (fontSelectorVisible.value === null && longPressIndex.value !== null && longPressElement.value) {
      hasLongPressed.value = true
      openFontSelector(longPressIndex.value, longPressElement.value)
    }
  }, longPressDelay)
}

const endLongPress = (event?: MouseEvent | TouchEvent) => {
  if (longPressTimer && !hasLongPressed.value) {
    clearTimeout(longPressTimer)
    longPressTimer = null
    longPressIndex.value = null
    longPressElement.value = null
  }
  
  if (hasLongPressed.value && event) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    
    setTimeout(() => {
      hasLongPressed.value = false
    }, 300)
  }
}

const handleFontDisplayClick = (index: number, event?: MouseEvent) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }
  
  if (hasLongPressed.value) {
    hasLongPressed.value = false
    return
  }
  
  if (fontSelectorVisible.value !== null) {
    return
  }
  
  selectPreset(index)
}

const openFontSelector = (index: number, element: HTMLElement) => {
  if (fontSelectorVisible.value === index) {
    fontSelectorVisible.value = null
    return
  }
  
  const rect = element.getBoundingClientRect()
  const panelRect = (element.closest('.text-panel') as HTMLElement)?.getBoundingClientRect()
  
  if (panelRect) {
    const centerY = panelRect.top + panelRect.height / 2
    const left = rect.left + rect.width / 2
    
    fontSelectorStyle.value = {
      top: `${centerY}px`,
      left: `${left}px`
    }
  }
  
  fontSelectorVisible.value = index
  
  nextTick(() => {
    if (fontSelectorRef.value) {
      const selectorHeight = fontSelectorRef.value.offsetHeight
      const currentTop = parseFloat(fontSelectorStyle.value.top)
      fontSelectorStyle.value = {
        ...fontSelectorStyle.value,
        top: `${currentTop - selectorHeight / 2}px`
      }
    }
  })
}

const selectFont = (fontFamily: string, event?: MouseEvent) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }
  
  if (fontSelectorVisible.value !== null) {
    const index = fontSelectorVisible.value
    if (textPresets.value[index]) {
      textPresets.value[index].fontFamily = fontFamily
      const currentColor = toolStore.currentTool.color
      textPresets.value[index].color = currentColor
      if (index === selectedIndex.value) {
        emit('text-style-selected', textPresets.value[index])
      }
    }
    fontSelectorVisible.value = null
    hasLongPressed.value = false
    longPressIndex.value = null
    longPressElement.value = null
  }
}

const closeFontSelector = (event: MouseEvent) => {
  if (hasLongPressed.value) {
    return
  }
  
  const target = event.target as Node
  
  if (fontSelectorRef.value && !fontSelectorRef.value.contains(target)) {
    const clickedOnFontDisplay = (target as HTMLElement).closest('.font-display')
    if (!clickedOnFontDisplay) {
      fontSelectorVisible.value = null
      hasLongPressed.value = false
      longPressIndex.value = null
      longPressElement.value = null
    }
  }
}

const handleInputBlur = (index: number) => {
  const value = Math.max(8, Math.min(200, inputValues.value[index] || 8))
  inputValues.value[index] = value
  if (textPresets.value[index]) {
    textPresets.value[index].fontSize = value
    const currentColor = toolStore.currentTool.color
    textPresets.value[index].color = currentColor
    if (index === selectedIndex.value) {
      emit('text-style-selected', textPresets.value[index])
    }
  }
}

watch(textPresets, () => {
  initializeInputValues()
}, { immediate: true, deep: true })

watch(selectedPreset, (newPreset) => {
  if (newPreset) {
    emit('text-style-selected', newPreset)
  }
}, { immediate: true })

watch(fontSelectorVisible, (visible) => {
  if (visible !== null) {
    nextTick(() => {
      setTimeout(() => {
        document.addEventListener('click', closeFontSelector, true)
        setTimeout(() => {
          hasLongPressed.value = false
        }, 200)
      }, 500)
    })
  } else {
    document.removeEventListener('click', closeFontSelector, true)
    hasLongPressed.value = false
  }
})

onMounted(() => {
  initializeInputValues()
  selectPreset(selectedIndex.value)
})

onUnmounted(() => {
  document.removeEventListener('click', closeFontSelector)
})</script>

<style scoped>
.text-panel-wrapper {
  position: relative;
}

.text-panel {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
  padding: 0;
  background: transparent;
  width: auto;
  min-width: fit-content;
}

.text-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 44px;
  height: 60px;
  background: transparent;
  cursor: pointer;
  gap: 4px;
  padding: 0;
  position: relative;
}

.text-item:hover {
  background: theme('colors.creme.DEFAULT');
}

.text-item.active .font-display {
  background: theme('colors.creme.lighter');
}

.font-display {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: theme('colors.text.DEFAULT');
  cursor: pointer;
  transition: background-color 0.15s ease;
  background: transparent;
}

.size-input {
  width: 2rem;
  text-align: center;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  outline: none;
  color: theme('colors.text.DEFAULT');
  font-size: 12px;
  height: 16px;
  line-height: 16px;
  appearance: textfield;
  -moz-appearance: textfield;
}

.size-input::-webkit-outer-spin-button,
.size-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.size-input:focus {
  background: transparent;
}

.font-selector {
  position: fixed;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px;
  background: theme('colors.white');
  border: 1px solid theme('colors.gray.300');
  border-radius: theme('borderRadius.sm');
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: translateX(-50%);
  max-height: 400px;
  overflow-y: auto;
}

.font-option {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: theme('colors.text.DEFAULT');
  cursor: pointer;
  border-radius: theme('borderRadius.sm');
  transition: background-color 0.15s ease;
}

.font-option:hover {
  background: theme('colors.creme.DEFAULT');
}

.font-option-active {
  background: theme('colors.creme.lighter');
}
</style>